import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { 
  ConsentPreferences, 
  ConsentState, 
  loadConsentState, 
  saveConsentState,
  initializeConsentMode,
} from '@/lib/tracking/consent-store';
import { loadTrackingScripts, unloadTrackingScripts } from '@/lib/tracking/script-loader';
import { initializeAttribution } from '@/lib/tracking/utm-manager';
import { useLanguage } from '@/hooks/useLanguage';

interface ConsentContextType {
  consentState: ConsentState;
  updateConsent: (preferences: ConsentPreferences) => Promise<void>;
  hasConsented: boolean;
  isAnalyticsAllowed: boolean;
  isMarketingAllowed: boolean;
  isPersonalizationAllowed: boolean;
}

const ConsentContext = createContext<ConsentContextType | undefined>(undefined);

interface ConsentProviderProps {
  children: ReactNode;
}

export const ConsentProvider = ({ children }: ConsentProviderProps) => {
  const { language } = useLanguage();
  const [consentState, setConsentState] = useState<ConsentState>(() => loadConsentState());

  useEffect(() => {
    // Initialize consent mode with denied state (GDPR compliant)
    initializeConsentMode();
    
    // Capture UTMs on initial load
    initializeAttribution();
    
    // Load current state
    const state = loadConsentState();
    setConsentState(state);
    
    // If already consented, load appropriate scripts
    if (state.hasConsented) {
      loadTrackingScripts(state.preferences);
    }
  }, []);

  const updateConsent = async (preferences: ConsentPreferences) => {
    await saveConsentState(preferences, language);
    
    // Reload scripts based on new preferences
    unloadTrackingScripts();
    loadTrackingScripts(preferences);
    
    // Update state
    setConsentState({
      hasConsented: true,
      preferences,
      timestamp: new Date().toISOString(),
      policyVersion: consentState.policyVersion,
    });
  };

  const value: ConsentContextType = {
    consentState,
    updateConsent,
    hasConsented: consentState.hasConsented,
    isAnalyticsAllowed: consentState.hasConsented && consentState.preferences.analytics,
    isMarketingAllowed: consentState.hasConsented && consentState.preferences.marketing,
    isPersonalizationAllowed: consentState.hasConsented && consentState.preferences.personalization,
  };

  return (
    <ConsentContext.Provider value={value}>
      {children}
    </ConsentContext.Provider>
  );
};

export const useConsent = (): ConsentContextType => {
  const context = useContext(ConsentContext);
  if (!context) {
    throw new Error('useConsent must be used within a ConsentProvider');
  }
  return context;
};

export default ConsentProvider;
